import React, { createContext, useContext } from "react";
import { makeStyles, Dialog, Box } from "@material-ui/core";

type ContextProps = {
  variant: "confirmation" | "response";
  color: "primary" | "secondary";
};

const WhitePopupContext = createContext<Partial<ContextProps>>({});

export const useTableOverviewContext = () => {
  return useContext(WhitePopupContext);
};

const SuccessIconSecondary = () => (
  <svg
    width="84"
    height="84"
    viewBox="0 0 84 84"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M61.1247 23.5833L33.6663 51.0417L18.708 36.125L12.833 42L33.6663 62.8333L66.9997 29.5L61.1247 23.5833ZM41.9997 0.333344C18.9997 0.333344 0.333008 19 0.333008 42C0.333008 65 18.9997 83.6667 41.9997 83.6667C64.9997 83.6667 83.6664 65 83.6664 42C83.6664 19 64.9997 0.333344 41.9997 0.333344ZM41.9997 75.3334C23.583 75.3334 8.66634 60.4167 8.66634 42C8.66634 23.5833 23.583 8.66668 41.9997 8.66668C60.4163 8.66668 75.333 23.5833 75.333 42C75.333 60.4167 60.4163 75.3334 41.9997 75.3334Z"
      fill="#EB5757"
    />
  </svg>
);

const SuccessIconPrimary = () => (
  <svg
    width="84"
    height="84"
    viewBox="0 0 84 84"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M61.1247 23.5834L33.6663 51.0417L18.708 36.125L12.833 42L33.6663 62.8334L66.9997 29.5L61.1247 23.5834ZM41.9997 0.333374C18.9997 0.333374 0.333008 19 0.333008 42C0.333008 65 18.9997 83.6667 41.9997 83.6667C64.9997 83.6667 83.6664 65 83.6664 42C83.6664 19 64.9997 0.333374 41.9997 0.333374ZM41.9997 75.3334C23.583 75.3334 8.66634 60.4167 8.66634 42C8.66634 23.5834 23.583 8.66671 41.9997 8.66671C60.4163 8.66671 75.333 23.5834 75.333 42C75.333 60.4167 60.4163 75.3334 41.9997 75.3334Z"
      fill="#19B586"
    />{" "}
  </svg>
);

const WaitingIconPrimary = () => (
  <svg
    width="84"
    height="84"
    viewBox="0 0 84 84"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M41.9583 0.333374C18.9583 0.333374 0.333313 19 0.333313 42C0.333313 65 18.9583 83.6667 41.9583 83.6667C65 83.6667 83.6667 65 83.6667 42C83.6667 19 65 0.333374 41.9583 0.333374ZM42 75.3334C23.5833 75.3334 8.66665 60.4167 8.66665 42C8.66665 23.5834 23.5833 8.66671 42 8.66671C60.4167 8.66671 75.3333 23.5834 75.3333 42C75.3333 60.4167 60.4167 75.3334 42 75.3334Z"
      fill="#19B586"
    />
    <path
      d="M44.0833 21.1666H37.8333V46.1666L59.7083 59.2916L62.8333 54.1666L44.0833 43.0416V21.1666Z"
      fill="#19B586"
    />
  </svg>
);

const AwareIconSecondary = () => (
  <svg
    width="84"
    height="84"
    viewBox="0 0 84 84"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M41.9583 83.6666C18.9519 83.6437 0.317989 64.9787 0.333322 41.9721C0.348656 18.9658 19.0074 0.325649 42.0137 0.333315C65.0204 0.340982 83.6667 18.9935 83.6667 42C83.6529 65.0225 64.9808 83.6758 41.9583 83.6666ZM8.66666 42.7166C8.86382 61.055 23.8379 75.7896 42.1771 75.6916C60.5167 75.5925 75.3312 60.6979 75.3312 42.3583C75.3312 24.0187 60.5167 9.12402 42.1771 9.02494C23.8379 8.92686 8.86382 23.6615 8.66666 42V42.7166ZM46.1667 62.8333H37.8333V54.5H46.1667V62.8333ZM46.1667 46.1667H37.8333V21.1666H46.1667V46.1667Z"
      fill="#EB5757"
    />
  </svg>
);

const useStyles = makeStyles((theme) => ({
  root: {
    // backgroundColor:"white",
  },
  paper: {
    backgroundColor: "white",
    margin: 0,
  },
  paperDark: {
    backgroundColor: "#141719",
    margin: 0,
  },
  paperFullWidth: {
    width: "100%",
    borderRadius: 8,
  },
}));

interface WhitePopupProps {
  open: boolean;
  onClose: () => void;
  variant: "confirmation" | "response";
  color: "primary" | "secondary";
  icon?:
    | "successPrimary"
    | "successSecondary"
    | "waitingPrimary"
    | "awareSecondary";
  useDark?: boolean;
}
const WhitePopup: React.FC<WhitePopupProps> = ({
  onClose,
  open,
  variant,
  icon,
  color,
  useDark,
  children,
}) => {
  const classes = useStyles();

  const WhitePopupStore = {
    variant: variant,
    color: color,
  };

  const paparProps = {
    classes: {
      root: useDark ? classes.paperDark : classes.paper,
    },
  };

  return (
    <WhitePopupContext.Provider value={WhitePopupStore}>
      <Dialog
        className={classes.root}
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="xs"
        PaperProps={paparProps}
        classes={{
          paperFullWidth: classes.paperFullWidth,
        }}
        // fullScreen={useMediaQuery(theme.breakpoints.down("sm"))}
      >
        <Box p={3}>
          {variant === "response" && (
            <Box display="flex" justifyContent="center" pb={3}>
              {!icon && color === "primary" && <SuccessIconPrimary />}
              {!icon && color === "secondary" && <SuccessIconSecondary />}
              {icon === "successPrimary" && <SuccessIconSecondary />}
              {icon === "successSecondary" && <SuccessIconSecondary />}
              {icon === "waitingPrimary" && <WaitingIconPrimary />}
              {icon === "awareSecondary" && <AwareIconSecondary />}
            </Box>
          )}
          {children}
        </Box>
      </Dialog>
    </WhitePopupContext.Provider>
  );
};

export default WhitePopup;
